import type { PropsWithChildren } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Button } from '~/components/Button';
import { cn } from '~/lib/cn';

export interface ErrorContentProps extends PropsWithChildren {
  type: 'default' | 'noAccess' | 'notFound';
  variant?: 'fullPage' | 'inline';
  fallbackRoute?: { href: string; label: string };
  description?: string;
}

export function ErrorContent({
  type,
  fallbackRoute,
  variant = 'fullPage',
  description,
}: ErrorContentProps) {
  const { t } = useTranslation('errors');

  const containerClassName = cn('flex flex-col justify-center bg-default', {
    '-m-xl h-svh': variant === 'fullPage',
    'm-0 h-full bg-transparent': variant === 'inline',
  });

  const subContainerClassName = cn(
    'flex size-full items-center whitespace-break-spaces marker:flex-col',
    { 'bg-gradient bg-cover bg-bottom bg-no-repeat': variant === 'fullPage' },
  );

  return (
    <div className={containerClassName} data-testid="container">
      <div className={subContainerClassName} data-testid="sub-container">
        <div className="mb-lg ml-3xl" data-testid="error-banner">
          <h1 className="m-0 font-header text-display-5xl uppercase leading-none">
            <Trans
              t={t}
              i18nKey={`${type}.header`}
              components={{ br: <br /> }}
            />
          </h1>
          <p className="mt-sm text-body-md">
            {description ? description : t(`${type}.description`)}
          </p>
          {fallbackRoute ? (
            <Button asChild size="large" variant="primary" className="mt-lg">
              <Link to={fallbackRoute.href}>{fallbackRoute.label}</Link>
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
ErrorContent.displayName = 'ErrorContent';
